import React, { useMemo, useState } from "react";
import { validators, formats, dates } from "investira.sdk";
import { Link } from "react-router-dom";
import { ATIVO_TIPO } from "@investirapri/financa.js/lib/enums";
import {
  Stack,
  Typography,
  IconButton,
  ButtonBase,
  Icon,
  Tooltip,
  Badge,
  Box,
} from "investira.react.components";
import { PATHS } from "../../enums";
import { PessoaImage } from "../../components/molecules";
import { BulkContextActions, DataTable } from "../../components/organisms";
import usePerfil from "../../hooks/usePerfil";

const AtivosDataGrid = (props) => {
  const PAGE_SIZE = 25;
  const { posicoes, mercado, ativoTipoId, actions } = props;

  const [rowSelection, setRowSelection] = useState({});

  const { usuarioPrioridade } = usePerfil();

  const bulkDeleteCarteiras = (pRows) => {
    const xPosicoes = pRows.map((xRow) => {
      return xRow.original;
    });

    actions.delete(xPosicoes);
  };

  const fields = useMemo(
    () => ({
      pessoa: {
        accessorKey: "emissor_pessoa_id",
        header: "",
        enableColumnActions: false,
        enableSorting: false,
        size: 50,
        Cell: ({ cell, row }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }

          const { emissor_pessoa_id, logo_pessoa_id, emissor } = row.original;
          const xPath = "/empresas";

          return (
            <Tooltip title={emissor?.pessoa_fantasia || emissor?.pessoa} arrow>
              <span>
                <ButtonBase
                  component={Link}
                  to={`${xPath}/${emissor_pessoa_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <PessoaImage
                    logoPessoaId={logo_pessoa_id}
                    alt={String(emissor_pessoa_id)}
                    size={36}
                  />
                </ButtonBase>
              </span>
            </Tooltip>
          );
        },
      },
      ativo: {
        accessorKey: "ativo",
        header: "Ativo",
        enableColumnActions: false,
        enableSorting: false,
        grow: true,
        muiTableHeadCellProps: {
          sx: {
            width: "100% !important",
          },
        },
        Cell: ({ cell, row }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }

          const { mercado, ifeed_ativo_id, marcacao } = row.original;
          const xPath = PATHS[mercado.toLowerCase()].path;

          return (
            <Badge
              badgeContent={marcacao}
              color="warning"
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <Box pr={1.5}>
                <Typography
                  color="primary"
                  variant="body2"
                  sx={{ fontWeight: 500 }}
                  component={Link}
                  to={`/ativos${xPath}/${ifeed_ativo_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {cell.getValue()}
                </Typography>
              </Box>
            </Badge>
          );
        },
      },
      ativo_tipo: {
        accessorKey: "ativo_tipo_id",
        header: "Ativo Tipo",
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ cell, row }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }

          // const { mercado, ifeed_ativo_id } = row.original;
          // const xPath = PATHS[mercado.toLowerCase()].path;

          return (
            <Typography color="textPrimary" variant="body2">
              {ATIVO_TIPO[cell.getValue()].descricao}
            </Typography>
          );
        },
      },
      ticker: {
        accessorKey: "ticker",
        header: "Ticker",
        enableColumnActions: false,
        enableSorting: false,
        size: 100,
      },
      isin: {
        accessorKey: "isin",
        header: "ISIN",
        enableColumnActions: false,
        enableSorting: false,
        size: 160,
      },
      quantidade: {
        accessorKey: "quantidade",
        header: "Quantidade",
        enableColumnActions: false,
        enableSorting: false,
        muiTableHeadCellProps: {
          align: "right",
        },
        muiTableBodyCellProps: {
          align: "right",
        },
        Cell: ({ cell, row }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return formats.formatNumber(
            cell.getValue(),
            row.original.decimais_quantidade || 0,
            true,
            false
          );
        },
      },
      pu: {
        accessorKey: "pu",
        header: "PU (R$)",
        enableColumnActions: false,
        enableSorting: false,
        muiTableHeadCellProps: {
          align: "right",
        },
        muiTableBodyCellProps: {
          align: "right",
        },
        Cell: ({ cell, row }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return formats.formatNumber(
            cell.getValue(),
            row.original.decimais,
            true,
            false
          );
        },
      },
      taxa: {
        accessorKey: "operacao_taxa",
        header: "Taxa (%)",
        enableColumnActions: false,
        enableSorting: false,
        size: 80,
        muiTableHeadCellProps: {
          align: "right",
        },
        muiTableBodyCellProps: {
          align: "right",
        },
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return formats.formatNumber(cell.getValue(), 2, true, false);
        },
      },
      financeiro: {
        accessorKey: "financeiro",
        header: "Financeiro (R$)",
        enableColumnActions: false,
        enableSorting: false,
        muiTableHeadCellProps: {
          align: "right",
        },
        muiTableBodyCellProps: {
          align: "right",
        },
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return formats.formatNumber(cell.getValue(), 2, true, false);
        },
      },
      codigo: {
        accessorKey: "ativo_auxid",
        header: "Código",
        enableColumnActions: false,
        enableSorting: false,
        size: 160,
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return cell.getValue();
        },
      },
      emissao: {
        accessorKey: "emissao",
        header: "Emissão",
        enableColumnActions: false,
        enableSorting: false,
        size: 120,
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return formats.formatDate(dates.toDate(cell.getValue()));
        },
      },
      vencimento: {
        accessorKey: "vencimento",
        header: "Vencimento",
        enableColumnActions: false,
        enableSorting: false,
        size: 120,
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return formats.formatDate(dates.toDate(cell.getValue()));
        },
      },
      serie: {
        accessorKey: "serie",
        header: "Série",
        enableColumnActions: false,
        enableSorting: false,
      },
      operacao_data: {
        accessorKey: "operacao_data",
        header: "Operação Data",
        enableColumnActions: false,
        enableSorting: false,
        size: 120,
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return;
          }
          return formats.formatDate(dates.toDate(cell.getValue()));
        },
      },
    }),
    []
  );

  const columns = {
    FND: [
      fields.pessoa,
      fields.ativo,
      fields.isin,
      fields.quantidade,
      fields.pu,
      fields.financeiro,
      fields.operacao_data,
      fields.codigo,
    ],
    TPU: [
      fields.pessoa,
      fields.ativo,
      fields.vencimento,
      fields.isin,
      fields.quantidade,
      fields.pu,
      fields.financeiro,
      fields.operacao_data,
      fields.taxa,
      fields.emissao,
      fields.serie,
      fields.codigo,
    ],
    TPR: [
      fields.pessoa,
      fields.ativo,
      fields.ticker,
      fields.isin,
      fields.quantidade,
      fields.pu,
      fields.financeiro,
      fields.operacao_data,
      fields.taxa,
      fields.emissao,
      fields.vencimento,
      fields.codigo,
    ],
    TBA: [
      fields.pessoa,
      fields.ativo,
      fields.ticker,
      fields.isin,
      fields.quantidade,
      fields.pu,
      fields.financeiro,
      fields.operacao_data,
      fields.taxa,
      fields.emissao,
      fields.vencimento,
      fields.codigo,
    ],
    ACN: [
      fields.pessoa,
      fields.ativo,
      fields.ticker,
      fields.isin,
      fields.quantidade,
      fields.pu,
      fields.financeiro,
      fields.operacao_data,
    ],
    DRI: [
      fields.pessoa,
      fields.ativo,
      fields.vencimento,
      fields.ticker,
      fields.isin,
      fields.quantidade,
      fields.pu,
      fields.financeiro,
      fields.operacao_data,
      fields.codigo,
    ],
    CRP: [
      fields.pessoa,
      fields.ativo,
      fields.ticker,
      fields.quantidade,
      fields.pu,
      fields.financeiro,
      fields.operacao_data,
    ],
    GEN: [
      fields.pessoa,
      fields.ativo,
      fields.ativo_tipo,
      fields.ticker,
      fields.isin,
      fields.quantidade,
      fields.pu,
      fields.financeiro,
      fields.operacao_data,
      fields.taxa,
      fields.emissao,
      fields.vencimento,
      fields.codigo,
    ],
  };

  const xProps = {
    data: posicoes,
    columns: mercado ? columns[mercado] : columns.GEN,
    state: {
      rowSelection: rowSelection,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: PAGE_SIZE,
      },
    },
    enableRowSelection: usuarioPrioridade <= 6,
    enablePagination: true,
    manualPagination: false,
    displayColumnDefOptions: {
      "mrt-row-actions": {
        header: "",
      },
    },
    getRowId: (row) => {
      return `${row.ifeed_ativo_id}-${crypto.randomUUID()}`;
    },
    renderTopToolbarCustomActions: ({ table }) => {
      if (usuarioPrioridade > 6) {
        return null;
      }

      const selectedRows = table.getSelectedRowModel().rows;
      const isSomeRowSelected = selectedRows.length > 0;

      return (
        <BulkContextActions
          onDeleteClick={() => {
            bulkDeleteCarteiras(selectedRows);
          }}
          disabled={!isSomeRowSelected}
        />
      );
    },
    renderRowActions: ({ row }) => {
      if (usuarioPrioridade > 6) {
        return null;
      }

      return (
        <Stack direction="row">
          <Tooltip title="Editar">
            <IconButton
              color="primary"
              onClick={() => actions.edit(row.original)}
            >
              <Icon iconName="edit" size={21} />
            </IconButton>
          </Tooltip>
        </Stack>
      );
    },
    onRowSelectionChange: setRowSelection,
  };

  return <DataTable key={`${mercado}-${ativoTipoId}`} {...xProps} />;
};

AtivosDataGrid.propTypes = {};

AtivosDataGrid.displayName = "AtivosDataGrid";

export default AtivosDataGrid;
